import { useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider
} from '@mui/material';
import type { CondicaoPagto, Parcela } from '@azoupvendas/shared';
import { usePedidoStore } from '../../stores/pedido.store';
import { ParcelasTable } from '../ParcelasTable';

// AIDEV-NOTE: Helpers de formatacao consistentes
function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
}

interface Step5ConfirmacaoProps {
  descontoGeral: number;
  observacao: string;
  origem: string;
  condicaoPagtoId: number | null;
  condicaoSelecionada: CondicaoPagto | null;
  parcelas: Parcela[];
  totalProdutos: number;
  totalComCondicao: number;
  tipoMovimentoReceberId?: number | null;
  tipoComercializacaoId?: number | null;
  // AIDEV-NOTE: Estado da mutation vem do PedidoWizard — botao fica no footer do wizard
  finalizarIsError: boolean;
  finalizarError: Error | null;
}

// AIDEV-NOTE: Step 5 do wizard — apenas exibe resumo. Botao "Finalizar Pedido" e
// o estado da mutation ficam no PedidoWizard (footer fixo ao lado do "Voltar").

export function Step5Confirmacao({
  descontoGeral,
  observacao,
  parcelas,
  condicaoSelecionada,
  totalProdutos,
  totalComCondicao,
  finalizarIsError,
  finalizarError
}: Step5ConfirmacaoProps) {
  const itens = usePedidoStore((s) => s.itens);
  const clienteSelecionado = usePedidoStore((s) => s.clienteSelecionado);

  const subtotal = useMemo(
    () =>
      itens.reduce(
        (acc, item) =>
          acc + item.valor_unitario * item.quantidade * (1 - (item.desconto_percentual ?? 0) / 100),
        0
      ),
    [itens]
  );

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        p: { xs: 2, sm: 3 },
        overscrollBehavior: 'contain',
        WebkitOverflowScrolling: 'touch'
      }}
    >
      <Typography variant="h5" sx={{ mb: 3 }}>
        Confirmar Pedido
      </Typography>

      {/* Resumo do cliente */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Cliente
        </Typography>
        <Typography variant="body1" fontWeight={500}>
          {clienteSelecionado?.RAZAO_SOCIAL ?? '--'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          CPF/CNPJ: {clienteSelecionado?.CNPJ_CPF ?? '--'}
        </Typography>
      </Paper>

      {/* Resumo de totais */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Resumo
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2">
            {itens.length} {itens.length === 1 ? 'item' : 'itens'}
          </Typography>
          <Typography variant="body2">{formatCurrency(subtotal)}</Typography>
        </Box>
        {descontoGeral > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" color="error.main">
              Desconto geral ({descontoGeral}%)
            </Typography>
            <Typography variant="body2" color="error.main">
              - {formatCurrency(subtotal * (descontoGeral / 100))}
            </Typography>
          </Box>
        )}
        {condicaoSelecionada && condicaoSelecionada.acrescimo > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" color="warning.main">
              Acrescimo ({condicaoSelecionada.acrescimo}%)
            </Typography>
            <Typography variant="body2" color="warning.main">
              + {formatCurrency(totalProdutos * (condicaoSelecionada.acrescimo / 100))}
            </Typography>
          </Box>
        )}
        {condicaoSelecionada && condicaoSelecionada.desconto > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" color="success.main">
              Desconto condicao ({condicaoSelecionada.desconto}%)
            </Typography>
            <Typography variant="body2" color="success.main">
              -{' '}
              {formatCurrency(
                totalProdutos *
                  (1 + condicaoSelecionada.acrescimo / 100) *
                  (condicaoSelecionada.desconto / 100)
              )}
            </Typography>
          </Box>
        )}
        <Divider sx={{ my: 1 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="h6" fontWeight={700}>
            Total Final
          </Typography>
          <Typography variant="h6" fontWeight={700} color="primary.main">
            {formatCurrency(totalComCondicao)}
          </Typography>
        </Box>
      </Paper>

      {/* Tabela simplificada de itens (sem edicao) */}
      <Paper sx={{ mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ p: 2, pb: 0 }}>
          Itens ({itens.length})
        </Typography>
        {(() => {
          // AIDEV-NOTE: usa codcor/codtamanho (0 = sem grade) pois cor/tamanho pode ser string
          // técnica mesmo em ERPs sem grade real (ex: "UN", "ÚNICO")
          const usaGrade = itens.some((i) => (i.codcor ?? 0) > 0 || (i.codtamanho ?? 0) > 0);
          return (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Produto</TableCell>
                    {usaGrade && <TableCell>Cor</TableCell>}
                    {usaGrade && <TableCell>Tam</TableCell>}
                    <TableCell align="right">Qtd</TableCell>
                    <TableCell align="right">Vlr Unit</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {itens.map((item, idx) => {
                    const totalItem =
                      item.valor_unitario *
                      item.quantidade *
                      (1 - (item.desconto_percentual ?? 0) / 100);
                    return (
                      <TableRow key={`${item.produto_codigo}-${item.cor}-${item.tamanho}-${idx}`}>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                            {item.descricao}
                          </Typography>
                        </TableCell>
                        {usaGrade && <TableCell>{item.cor ?? '--'}</TableCell>}
                        {usaGrade && <TableCell>{item.tamanho ?? '--'}</TableCell>}
                        <TableCell align="right">{item.quantidade}</TableCell>
                        <TableCell align="right">{formatCurrency(item.valor_unitario)}</TableCell>
                        <TableCell align="right">{formatCurrency(totalItem)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          );
        })()}
      </Paper>

      {/* Parcelas */}
      {parcelas.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Parcelas
          </Typography>
          <ParcelasTable parcelas={parcelas} />
        </Box>
      )}

      {/* Observacao */}
      {observacao && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Observacao
          </Typography>
          <Typography variant="body2">{observacao}</Typography>
        </Paper>
      )}

      {/* Erro de finalizacao (mutation no PedidoWizard) */}
      {finalizarIsError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {finalizarError?.message ?? 'Erro ao finalizar pedido. Tente novamente.'}
        </Alert>
      )}
    </Box>
  );
}
